import { createSelector, createSlice } from "@reduxjs/toolkit";
import service from "../service";
import { popPreviousToken, pushPreviousToken, reset, resetPagination } from "./globalActions";
import { generateClient } from 'aws-amplify/api';
import * as queries from "../graphql/queries";
import * as mutations from "../graphql/mutations";
import * as customQueries from "../graphql/customQueries";
import { getCurrentUser } from "aws-amplify/auth";

const client = generateClient();

const initialState = {
    initialized: false,
    list: [],
    nextToken: null,
    previousTokens: [],
    loading: false,
    user: null,
};

const userSlice = createSlice({
    name: "user",
    initialState,
    extraReducers: (builder) => {
        builder.addCase(reset, (state) => {
            return {
                ...initialState,
                initialized: state.initialized,
            };
        });
        builder.addCase(resetPagination, (state) => {
            state.nextToken = null;
            state.previousTokens = [];
        });
        builder.addCase(popPreviousToken, (state) => {
            state.previousTokens.pop();
        });
        builder.addCase(pushPreviousToken, (state, action) => {
            state.previousTokens.push(action.payload);
        });
    },
    reducers: {
        setInitialized: (state, action) => {
            state.initialized = true;
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        onUpdateList: (state, action) => {
            state.list = action.payload.items;
            state.nextToken = action.payload.nextToken;
        },

        onUpdateUser: (state, action) => {
            if (!state.user || state.user?.id === action.payload?.id) {
                state.user = action.payload;
            }

            let idx = state.list.findIndex((u) => u?.id === action.payload?.id);
            if (idx >= 0) state.list[idx] = action.payload;
            else state.list.unshift(action.payload);
        },

        onDeleteUser: (state, action) => {
            let idx = state.list.findIndex((u) => u.id === action.payload);
            if (idx >= 0) state.list = [...state.list.slice(0, idx), ...state.list.slice(idx + 1)];
        },
    },
});

export const { setInitialized, setLoading, onUpdateUser, onDeleteUser, onUpdateList } = userSlice.actions;
export default userSlice.reducer;

export const selectState = (state) => state.user;
export const selectUser = createSelector(selectState, (state) => state.user);
export const selectUsers = createSelector(selectState, (state) => state.list);
export const selectLoading = createSelector(selectState, (state) => state.loading);
export const selectPagination = createSelector(selectState, (state) => ({ nextToken: state.nextToken, previousTokens: state.previousTokens }));
export const selectUserById = createSelector([selectUsers, (state, id) => id], (users, id) => {
    if (!id) return null;
    return users.find((u) => u.id === id);
});

export const fetchUser = () => async (dispatch, getState) => {
    try {
        const { username } = await getCurrentUser();
        let user = await service.getUser(username);
        dispatch(onUpdateUser(user));
        return user;
    } catch (ex) {
        console.log("failed to fetch user", ex.message);
    }
};

export const fetchListUsers =
    (locationId, nextToken, storeNextToken = true) =>
        async (dispatch) => {
            try {
                if (storeNextToken) dispatch(pushPreviousToken(nextToken));
                else dispatch(popPreviousToken());

                const { data } = await client.graphql({
                    query: customQueries.usersByLocationId,
                    variables: {
                        locationId,
                        nextToken,
                        limit: 12,
                    },
                });
                dispatch(onUpdateList(data.usersByLocationId));
            } catch (ex) {
                console.log("Failed to list users", ex);
            }
        };

export const searchUsers = (locationId, search, nextToken, storeNextToken = true) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        if (storeNextToken) dispatch(pushPreviousToken(nextToken));
        else dispatch(popPreviousToken());

        const { data } = await client.graphql({
            query: customQueries.searchUsers,
            variables: {
                nextToken,
                limit: 12,
                filter: {
                    or: [{ firstName: { wildcard: `*${search}*` } }, { lastName: { wildcard: `*${search}*` } }, { email: { wildcard: `*${search}*` } }],
                    and: [{ locationId: { eq: locationId } }]
                },
                sort: [{ field: 'firstName', direction: 'asc' }]
            },
        });
        dispatch(onUpdateList(data.searchUsers));
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchUserById = (id) => async (dispatch) => {
    const { data } = await client.graphql({
        query: queries.getUser,
        variables: {
            id,
        },
    });
    if (!data.getUser) throw new Error("User Doesn't Exist")
    dispatch(onUpdateUser(data.getUser));
};

export const createUser =
    ({ firstName, lastName, crewMemberId, locationId, email, role, phoneNumber }) =>
        async (dispatch) => {
            const { data } = await client.graphql({
                query: mutations.adminCreateUser,
                variables: {
                    input: {
                        firstName,
                        lastName,
                        email,
                        role,
                        phoneNumber,
                        locationId,
                        crewMemberId,
                    },
                },
            });
            if(!data.adminCreateUser) throw new Error("Failed to create user, it is possible there is a duplicate email address already in the system")
            dispatch(onUpdateUser(data.adminCreateUser));
            return data.adminCreateUser;
        };

export const updateUser =
    ({ id, firstName, lastName, crewMemberId, locationId, email, role, phoneNumber, expoPushToken, isNotificationEnabled }) =>
        async (dispatch) => {
            const { data } = await client.graphql({
                query: mutations.updateUser,
                variables: {
                    input: {
                        id,
                        firstName,
                        lastName,
                        email,
                        role,
                        phoneNumber,
                        locationId,
                        crewMemberId,
                        isNotificationEnabled,
                        expoPushToken
                    },
                },
            });
            dispatch(onUpdateUser(data.updateUser));
            return data.updateUser;
        };

export const deleteUser = (user) => async (dispatch) => {
    const { data } = await client.graphql({
        query: mutations.adminDeleteUser,
        variables: { input: user },
    });
    if (!data.adminDeleteUser) throw new Error("Failed to delete user, user may have already been deleted");
    dispatch(onDeleteUser(user.id));
};

export const setUserPassword = (user) => async (dispatch) => {
    const { data } = await client.graphql({
        query: mutations.setUserPassword,
        variables: { input: user },
    });
    const { status, message } = data.setUserPassword
    if (!status) throw new Error(message);
    return status;
};

export const sendHeadsUpMessage = (params) => async () => {
    const { data } = await client.graphql({
        query: mutations.sendHeadsUpMessage,
        variables: {
            input: params
        }
    });
    const { status, message } = data.sendHeadsUpMessage;
    if (!status) throw new Error(message);
    return status;

}

export const createRateOverride = ({ crewMemberId, locationId, rate }) => async () => {
    const { data } = await client.graphql({
        query: mutations.createRateOverride,
        variables: {
            input: {
                crewMemberId,
                locationId,
                rate
            }
        }
    });

    return data.createRateOverride;
}

export const updateRateOverride = ({ crewMemberId, locationId, rate }) => async () => {
    const { data } = await client.graphql({
        query: mutations.updateRateOverride,
        variables: {
            input: {
                crewMemberId,
                locationId,
                rate
            }
        }
    });

    return data.updateRateOverride;
}
